import { tokens } from '@/theme'
import { MOBILE_MAX } from '@/components/layout/responsive'
import type { AdminSettings } from '@/domain/types'
import type { AdminTab } from '@/views/AdminView'

/**
 * Values the admin panels and their skeletons both draw with, so a skeleton
 * lands on the same widths, labels and rules as the panel that replaces it.
 */

/** AdminView and AdminPageSkeleton: the sidebar folds over the panel at phone width. */
export const adminGridStyles = `
@media (max-width: ${MOBILE_MAX}px) {
  .keystone-admin-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 24px !important; }
  .keystone-admin-grid > aside { position: static !important; }
}
`

/** The gold group heading above each run of sidebar tabs. */
export const groupHeading = {
  fontSize: 10.5,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: tokens.gold,
  marginBottom: 6,
  paddingLeft: 14,
} as const

/** The admin sidebar: group headings and tabs, in order. Counts come from the view. */
export const ADMIN_GROUPS: { heading: string; items: { id: AdminTab; label: string }[] }[] = [
  {
    heading: 'Organisation',
    items: [
      { id: 'countries', label: 'Countries' },
      { id: 'regions', label: 'Regions' },
    ],
  },
  {
    heading: 'Absence rules',
    items: [
      { id: 'holidays', label: 'Public holidays' },
      { id: 'categories', label: 'Absence categories' },
      { id: 'week', label: 'Working week' },
    ],
  },
  {
    heading: 'People',
    items: [{ id: 'teams', label: 'Teams' }],
  },
  {
    heading: 'System',
    items: [
      { id: 'bugs', label: 'Bug reports' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'tutorials', label: 'Tutorials' },
    ],
  },
]

/** Each panel's title and lede, for the panel and its skeleton. */
export const PANEL_HEADS = {
  bugs: {
    title: 'Bug reports',
    lede: 'What people reported from the bug icon in the header. Each reporter sees their own reports, with the status and comments set here.',
  },
  categories: {
    title: 'Absence categories',
    lede: 'Colours are used on every timeline. Requires approval decides whether a request goes to a manager as Pending or is approved on submission. Confidential pre-ticks “Mark as sensitive”. Removing a category deactivates it; existing requests keep it.',
  },
  holidays: {
    title: 'Public holidays',
    lede: 'Imported per country and year from Nager.Date, then edited here. A holiday on someone’s working day comes off their free hours and shows on every timeline.',
  },
  tutorials: {
    title: 'Tutorials',
    lede: 'Videos everyone can watch under Settings > Help, in this order. The welcome video pops up the first time someone signs in, until they close it. MP4 plays in every browser.',
  },
  notifications: {
    title: 'Notifications',
    lede: 'Email is sent through the notification webhook (Logic App).',
  },
  week: {
    title: 'Working week',
    lede: 'How Keystone counts free hours on allocation grids and the People grid.',
  },
  teams: {
    title: 'Teams',
    lede: 'Only team members, and people already allocated, show on the People grid and in an initiative’s “Who is free in Wxx” panel. A team’s manager is shown for reference; time-off requests go to each person’s own manager.',
  },
} as const satisfies Record<string, { title: string; lede: string }>

/** HolidaysPanel: the imported / needs-import country boxes. */
export const box = {
  border: `1px solid ${tokens.line}`,
  borderRadius: tokens.radiusMd,
  padding: '14px 16px',
} as const

/** CategoriesPanel: the three checkbox flags, in column order. */
export type Flag = 'confidential' | 'countsTowardBalance' | 'requiresApproval'

export const FLAGS: { key: Flag; label: string }[] = [
  { key: 'confidential', label: 'Confidential' },
  { key: 'countsTowardBalance', label: 'Counts toward balance' },
  { key: 'requiresApproval', label: 'Requires approval' },
]

/** CategoriesPanel: the table's column template. */
export const GRID = '52px minmax(0,1.3fr) 100px 110px 100px 80px 96px'

/** TutorialsPanel: order, title, file, welcome, remove. */
export const TUTORIAL_GRID = '64px minmax(0,1.4fr) minmax(0,1fr) 72px 150px'

/** NotificationsPanel: the switches, in row order. */
export const OPTIONS: { key: keyof AdminSettings['notifications']; label: string }[] = [
  { key: 'approvals', label: 'Email the manager when a time-off request is submitted' },
  { key: 'bugs', label: 'Email admins when a bug is reported' },
  {
    key: 'overlap',
    label: 'Email initiative owners when approved absence overlaps one of their allocations',
  },
]

/** WorkingWeekPanel: how free hours are counted. Only the hours per day is a setting; the rest says where each input lives. */
export const RULES: [string, string][] = [
  ['Working days', 'Per person, in Settings > Working time. Monday to Friday unless changed.'],
  [
    'Public holidays',
    'From the person’s home country (Settings > Country) and their site’s region.',
  ],
  ['Approved absence', 'Every category comes off free hours, business travel included.'],
  ['Pending requests', 'Shown on timelines in a lighter tone. Not subtracted until approved.'],
]

/** WorkingWeekPanel: one ruled row of the settings list. */
export const row = {
  display: 'grid',
  gridTemplateColumns: '220px minmax(0,1fr)',
  gap: 14,
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: `1px solid ${tokens.line}`,
  fontSize: 14,
} as const
